import { Subject } from 'rxjs';
import { filter, pluck } from 'rxjs/operators';

class CommunicationAction {
    constructor(payload, type) {
        this._payload = payload;
        this._type = type;
    }
    get payload() {
        return this._payload;
    }
    get type() {
        return this._type;
    }
}

const languageChangeActionType = 'language-change';
class LanguageChangeAction extends CommunicationAction {
    constructor(payload) {
        super(payload, languageChangeActionType);
    }
}

/**
 * A communication channel is a scoped space for each application.
 */
class CommunicationChannel {
    constructor(scope, messageBus) {
        this.scope = scope;
        this.messageBus = messageBus;
    }
    /**
     * Registers an event into the main Event bus to be handled by the host application.
     * @param action The action to dispatch.
     * @returns void.
     */
    dispatch(action) {
        this.messageBus.dispatch(action, this.scope, this.messageBus.host);
    }
    /**
     * Registers and event into the main Event bus and broadcasts the event for all the available scopes.
     * @param action The action to dispatch.
     * @returns void.
     */
    broadcast(action) {
        this.messageBus.dispatch(action, this.scope, '*');
    }
    /**
     * Subscribes to an action.
     * @param action The action to listen to.
     * @returns The data associated with the given action.
     */
    on(action) {
        return this.messageBus.on(action, this.scope);
    }
    /**
     * Subscribes to all the events fired to a specific channel.
     * @returns {Object} A CommunicationEvent instance each time a new event is emitted to the selected channel.
     */
    all() {
        return this.messageBus.allFrom(this.scope);
    }
    /**
     * This event is fired whenever the language is changed in the host application.
     * @returns {Object} The current language.
     */
    languageChanged() {
        return this.on(languageChangeActionType);
    }
    /**
     * Removes the Channel from the Event bus.
     * @returns void
     */
    destroy() {
        this.messageBus.unloadChannel(this.scope);
    }
}

/**
 * The communcation class is the Event bus.
 * It stores the different registered channels and provides methods to both emit and handle events.
 *
 * There should be only one instance across an application.
 */
class Communication {
    constructor(host) {
        // The subject which stores the events.
        this.feed$$ = new Subject();
        // Registered channels during an application lyfecicle.
        this.channels = {};
        this.applicationId = host;
        Communication.instances.push(this);
    }
    /**
     * Gets the current application id.
     */
    get host() {
        return this.applicationId;
    }
    /**
     * Emits an action to a scope.
     * @param action The action to dispatch.
     * @param scope Scope that triggers the event.
     * @param scopeTo Scope where the event is emitted to.
     * @returns void.
     */
    dispatch(data, scope, scopeTo = this.applicationId) {
        this.feed$$.next({
            action: data.type,
            scopes: {
                from: scope,
                to: scopeTo
            },
            payload: data.payload
        });
    }
    /**
     * Creates a logical partition of topics.
     * @param scope The channel name.
     * @returns {Object} The communcation channel for a given scope.
     */
    channel(scope) {
        if (!this.channels[scope]) {
            this.channels[scope] = new CommunicationChannel(scope, this);
        }
        return this.channels[scope];
    }
    /**
     * Subscribes to an action.
     * @param action The action to listen to.
     * @param scope The scope to which the event is emitted to.
     * @returns The data associated with the given action.
     */
    on(action, scope) {
        return this.feed$$.asObservable().pipe(filter(data => data.action === action), filter(data => data.scopes.to === scope || data.scopes.to === '*'), pluck('payload'));
    }
    /**
     * Subscribes to all the events fired to a specific channel.
     * @param scope The channel to listen to.
     * @returns {Object} A CommunicationEvent each time a new event is emitted to the selected channel.
     */
    allFrom(scope) {
        return this.feed$$.asObservable().pipe(filter(data => data.scopes.to === scope));
    }
    /**
     * Unloads a channel from the main event bus.
     * @param scope The channel to be destroyed.
     * @returns void;
     */
    unloadChannel(scope) {
        if (this.channels[scope]) {
            delete this.channels.scope;
        }
    }
}
Communication.instances = [];

let communicationService;
function registerBus(host) {
    if (!isThatBusRegistered(host)) {
        communicationService = new Communication(host);
    }
}
function isThatBusRegistered(host) {
    const instaceIterator = Communication.instances.values();
    for (const value of instaceIterator) {
        if (value.applicationId === host) {
            return true;
        }
    }
    return false;
}
function loadChannel(scope) {
    return communicationService.channel(scope);
}

const loadVdrActionType = 'load-vdr';
class LoadVdrAction extends CommunicationAction {
    constructor(payload) {
        super(payload, loadVdrActionType);
    }
}

const changeSubsidiaryActionType = 'change-subsidiary';
const changeSubsidiaryFullInfoActionType = 'change-subsidiary-full-info';
class ChangeSubsidiaryAction extends CommunicationAction {
    constructor(payload) {
        super(payload, changeSubsidiaryActionType);
    }
}
class ChangeSubsidiaryFullInfoAction extends CommunicationAction {
    constructor(payload) {
        super(payload, changeSubsidiaryFullInfoActionType);
    }
}

const LoadAppPermissionActionType = 'load-apppermission';
class LoadApppermissionAction extends CommunicationAction {
    constructor(payload) {
        super(payload, LoadAppPermissionActionType);
    }
}

/**
 * Generated bundle index. Do not edit.
 */

export { ChangeSubsidiaryAction, ChangeSubsidiaryFullInfoAction, CommunicationChannel, LanguageChangeAction, LoadAppPermissionActionType, LoadApppermissionAction, LoadVdrAction, changeSubsidiaryActionType, changeSubsidiaryFullInfoActionType, languageChangeActionType, loadChannel, loadVdrActionType, registerBus };
